import { put, takeLatest, delay, call, all, select } from 'redux-saga/effects';
import {
  GET_PROFILE,
  SAVE_PROFILE,
  GET_NUTRITION,
  SAVE_NUTRITION,
  SAVE_PROFILE_IGNORE_NUTRITION,
  ADD_WEIGHT_TO_PROFILE,
  GET_ALL_ONSITE_CLIENTS,
  GET_FITBIT_NUTRITION,
  GET_ALL_NUTRITION,
  GET_USER_FITBIT_LOGS,
  CREATE_USER_MEAL_PLAN,
  CREATE_WEEKLY_MEAL_PLAN,
  GET_EQUIVALENT_RECIPES,
  SWAP_MEAL_PLAN_RECIPE,
  UPDATE_FOOD_IN_MEAL_PLAN,
  GET_ALTERNATIVE_FOODS,
  UPDATE_ASSESSMENT_PROFILE,
  CREATE_NEW_USER,
} from './constants';
import {
  getCurrentProfileSuccess,
  getCurrentProfileError,
  saveProfileSuccess,
  saveProfileError,
  getNutritionSuccess,
  getNutritionError,
  saveNutritionSuccess,
  saveNutritionError,
  addWeightToProfileSuccess,
  addWeightToProfileError,
  getAllOnsiteClientsSuccess,
  getAllOnsiteClientsError,
  getUserFitbitNutritionSuccess,
  getAllNutritionSuccess,
  getAllNutritionError,
  getUserFitbitLogsSuccess,
  getUserFitbitLogsError,
  createUserMealPlanSuccess,
  createUserMealPlanError,
  createWeeklyMealPlanSuccess,
  createWeeklyMealPlanError,
  getEquivalentRecipesSuccess,
  getEquivalentRecipesError,
  swapMealPlanRecipeSuccess,
  swapMealPlanRecipeError,
  updateFoodInMealPlanSuccess,
  updateFoodInMealPlanError,
  getAlternativeFoodsSuccess,
  getAlternativeFoodsError,
  updateFoodInMealPlanLoadingSuccess,
  createNewUser,
  createNewUserSuccess,
  createNewUserError,
} from './actions';
import { loadUser } from '../Auth/saga';
import { fetchData, postData } from '../api';
import { poundsToKgs, kgsToPounds } from '../../helpers/utils';
import { store } from '../../App';
import { getAllUserNotifications } from '../Notifications/actions';
import { updateUserProfileError, updateUserProfileSuccess } from './actions';
import { UPDATE_USER_PROFILE } from './constants';
import { toast } from '../../components/Toast';

export function* getCurrentProfile() {
  const url = `${process.env.REACT_APP_API_URL}/api/profile/me`;
  try {
    const config = {
      headers: {
        'x-auth-token': window.localStorage.token,
      },
    };
    let payload = yield call(fetchData, url, config);
    // Trainers (and any other role without a Profile doc) get a
    // legitimate `200 null` from /api/profile/me — skip the Withings
    // sub-flow and dispatch success so the reducer can set
    // `profile: null` and downstream pages render their no-profile
    // states. Previously this saga crashed on `null.withings`, which
    // took out every other Profile-saga task with it.
    if (payload && payload.withings && payload.withings.accessToken) {
      // Update Withings weight - backend will recalculate nutrition automatically
      try {
        const withingsUrl = `${process.env.REACT_APP_API_URL}/api/withings/updateUserWeight`;
        const withingsConfig = {
          method: 'post',
          headers: {
            'x-auth-token': window.localStorage.token,
          },
        };
        const withingsResponse = yield call(fetchData, withingsUrl, withingsConfig);
        
        // If weight was updated, refresh nutrition after backend async calculation
        // Backend returns: { success: true, newMeasurements: number }
        // newMeasurements > 0 means weight was updated
        if (withingsResponse && withingsResponse.success && withingsResponse.newMeasurements > 0) {
          // Weight was updated. Only refresh nutrition + claim a macro recalc
          // when the client actually has nutrition on — otherwise the refresh
          // just 403s and the toast contradicts "nutrition is off". The weight
          // itself still synced (and the server keeps macros current for a
          // possible re-enable), so we still acknowledge the weight update.
          const authUser = yield select((s) => s.auth.user);
          const nutritionOn =
            authUser?.enabledModules?.nutrition !== false;
          if (nutritionOn) {
            yield delay(600);
            yield put({ type: GET_NUTRITION });
          }
          toast.success(
            nutritionOn
              ? '✅ Weight updated from Withings. Your macros have been recalculated.'
              : '✅ Weight updated from Withings.',
            {
              position: 'top-right',
              duration: 5000,
            }
          );
        }
      } catch (withingsError) {
        // Silently fail - Withings update is not critical for profile loading
        console.log('Withings weight update error:', withingsError);
      }
    }
    yield put(getCurrentProfileSuccess(payload));
  } catch (err) {
    // The original catch called `err.json()` unconditionally and
    // then re-fetched without a second try/catch — any non-Response
    // error (e.g. the TypeError this saga used to throw on null
    // payload) would compound and crash the entire profile saga
    // tree, taking out every other takeLatest in this file. Defensive
    // shape: always parse safely, always dispatch ERROR, never
    // re-throw without a guard.
    console.error('getCurrentProfile failed:', err);
    let errorPayload = null;
    if (err && typeof err.json === 'function') {
      try {
        errorPayload = yield err.json();
      } catch (_) {
        errorPayload = {
          status: err.status,
          message: err.statusText || 'Unknown error',
        };
      }
    } else {
      errorPayload = { message: err?.message || String(err) };
    }
    yield put(getCurrentProfileError(errorPayload));
  }
}

export function* saveProfileIgnoreNutrition(payload) {
  const token = localStorage.token;
  // Two callers, two shapes: ProfileForm dispatches the flat profile, while
  // ProfileSettingsPage (the /dashboard/settings measurement toggle) double-wraps
  // it as { profileData, isFromSettings: true }. Unwrap the settings shape so the
  // POST body is always the flat profile the /api/profile validators expect —
  // otherwise body.age / goalWeight / macros are all undefined and validation fails.
  const isFromSettings = !!payload.profileData?.isFromSettings;
  const body = isFromSettings
    ? payload.profileData.profileData
    : payload.profileData;
  const ignoreNutrition = body?.ignoreNutrition;
  
  // Check if weight changed BEFORE saving profile (use current profile from Redux)
  const currentProfile = yield select((state) => state.profile.profile);
  const hasWeightChanged = checkIfWeightChanged(body, currentProfile);
  
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/profile`;
    const config = {
      method: 'POST',
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    };
    const payload = yield call(fetchData, url, config);
    yield put(
      saveProfileSuccess({
        profile: payload,
        // isFromSettings
      })
    );
    showToasterForProfileUpdate(
      '🎉 Your macronutrients data has been updated!'
    );

    if (!ignoreNutrition) {
      if (hasWeightChanged) {
        // Weight changed - let backend recalculate nutrition via addWeightToProfile
        // Don't save frontend nutrition as backend will overwrite it
        yield call(addWeightToProfile, body);
      } else {
        // Weight didn't change - save frontend-calculated nutrition
        // (User might have manually adjusted macros or other non-weight changes)
        const nutrition = yield select((state) => state.profile.nutrition);
        yield call(saveNutrition, nutrition);
      }
    }
  } catch (err) {
    console.log(err);
    const error = yield err.json();
    console.log(error);
    yield put(saveProfileError(error));
  }
}

// Helper function to check if weight changed (same logic as addWeightToProfile)
function checkIfWeightChanged(profileData, currentProfile) {
  // If no currentWeight in profileData, weight didn't change
  if (!profileData.currentWeight) {
    return false;
  }

  // Calculate new weight values
  let weightKg = profileData.isMetric
    ? profileData.currentWeight
    : Number(poundsToKgs(profileData.currentWeight));
  let weightLb = !profileData.isMetric
    ? profileData.currentWeight
    : Number(kgsToPounds(profileData.currentWeight));

  // Check against current profile weight (same logic as addWeightToProfile)
  if (!currentProfile || !currentProfile.weight || !currentProfile.weight.length) {
    // No existing weight - this would be a new weight entry
    // Let addWeightToProfile handle it, but we should still let backend recalculate
    return true;
  }

  const existingWeight = currentProfile.weight[0];
  const weightChanged = 
    +weightKg !== existingWeight.weightKg ||
    weightLb !== existingWeight.weightLb;

  return weightChanged;
}

export function* saveProfile(payload) {
  const token = localStorage.token;
  let { profileData } = payload;
  try {
    const config = {
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json',
      },
    };
    const url = `${process.env.REACT_APP_API_URL}/api/profile`;
    const responseData = yield call(() =>
      postData(url, config, profileData.profileData)
    );
    yield put(saveProfileSuccess(responseData.data));
    showToasterForProfileUpdate();
    yield call(saveNutrition, profileData);
  } catch (err) {
    const error = yield err.json();
    console.log(error);
    yield put(saveProfileError(error));
  }
}

export function* getAllNutritionForUser() {
  const token = localStorage.token;

  try {
    let payload = yield call(
      fetchData,
      `${process.env.REACT_APP_API_URL}/api/nutrition/me/all`,
      {
        headers: {
          'x-auth-token': token,
        },
      }
    );
    yield put(getAllNutritionSuccess(payload));
  } catch (err) {
    const error = yield err.json();
    console.log(error);
    yield delay(2000);
    let payload = yield call(
      fetchData,
      `${process.env.REACT_APP_API_URL}/api/nutrition/me/all`,
      {
        headers: {
          'x-auth-token': window.localStorage.token,
        },
      }
    );
    yield put(getAllNutritionError(payload));
  }
}

export function* getNutrition() {
  const token = localStorage.token;

  try {
    let payload = yield call(
      fetchData,
      `${process.env.REACT_APP_API_URL}/api/nutrition/me`,
      {
        headers: {
          'x-auth-token': token,
        },
      }
    );
    yield put(getNutritionSuccess(payload));
  } catch (err) {
    // A 403 = the nutrition module is disabled for this client — a PERMANENT
    // state, not a transient failure. Don't retry (a retrying 403 was
    // previously UNCAUGHT and cancelled the entire profile saga tree); just
    // resolve to empty nutrition.
    if (err && err.status === 403) {
      yield put(getNutritionSuccess(null));
      // The trainer disabled nutrition mid-session, so our cached
      // enabledModules is stale (still "on") — the nav + route guard don't know
      // yet, and the nutrition page hangs on <Loading/> waiting for data the
      // server now 403s. The 403 is the real-time "it's off now" signal:
      // refresh the session so enabledModules flips false, which hides the
      // nutrition nav and lets PrivateRoute redirect off nutrition routes to
      // /dashboard/home instead of spinning forever. Guard so we only refetch
      // when the state is actually stale (no needless re-fetch once it's off).
      const authUser = yield select((s) => s.auth.user);
      if (authUser?.enabledModules?.nutrition !== false) {
        yield call(loadUser);
      }
      return;
    }
    // Other errors: retry once, but WRAPPED so a repeat failure can never crash
    // the saga tree.
    try {
      const error = yield err.json();
      console.log(error);
      yield delay(2000);
      const payload = yield call(
        fetchData,
        `${process.env.REACT_APP_API_URL}/api/nutrition/me`,
        {
          headers: {
            'x-auth-token': window.localStorage.token,
          },
        }
      );
      yield put(getNutritionSuccess(payload));
    } catch (retryErr) {
      console.warn(
        'getNutrition failed after retry',
        retryErr?.status || retryErr
      );
      // Dispatch ERROR (not success(null)) so a transient double-failure on a
      // background refresh keeps the last-good nutrition instead of nulling it.
      // Nulling would flip home.js's notLoading gate false and unmount the
      // already-rendered dashboard to a stuck spinner. GET_NUTRITION_ERROR only
      // sets loading:false + error and leaves `nutrition` untouched, while still
      // settling the saga so it can never crash the tree.
      yield put(getNutritionError(retryErr));
    }
  }
}

export function* saveNutrition(action) {
  const token = localStorage.token;
  try {
    // Two call styles: dispatched via takeLatest (action shape is
    // `{ type, nutrition: { nutritionResults, callback? } }`) OR
    // invoked directly with `yield call(saveNutrition, nutrition)`
    // from saveProfileIgnoreNutrition (arg shape is
    // `{ nutritionResults, callback? }`). Resolve the data object
    // from whichever path carries it.
    const data = (action && action.nutrition) || action || {};
    const config = {
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json',
      },
    };
    const url = `${process.env.REACT_APP_API_URL}/api/nutrition`;
    yield call(() => postData(url, config, data.nutritionResults));
    yield put(saveNutritionSuccess(data.nutritionResults));
    // Invalidate the macro-history cache so the new calculation shows
    // up the next time the user visits /dashboard/tools/macro-history.
    // The history page now skips its on-mount fetch when cached, so
    // this dispatch is what keeps the cache in sync after a recalc.
    yield put({ type: GET_ALL_NUTRITION });
    typeof data.callback === 'function' && data.callback(); //Redirects to dashboard only called when creating an account
  } catch (err) {
    const error = yield err.json();
    console.log(error);
    yield put(saveNutritionError(error));
  }
}

export function* addWeightToProfile(profileData) {
  let weightKg = profileData.isMetric
    ? profileData.currentWeight
    : Number(poundsToKgs(profileData.currentWeight));
  let weightLb = !profileData.isMetric
    ? profileData.currentWeight
    : Number(kgsToPounds(profileData.currentWeight));
  
  // Include bodyFatPercentage if available
  let weight = { 
    weightKg: weightKg, 
    weightLb: weightLb 
  };
  
  if (profileData.bodyFatPercentage !== undefined && profileData.bodyFatPercentage !== null) {
    weight.bodyFatPercentage = profileData.bodyFatPercentage;
  }
  
  if (
    !profileData.weight.length ||
    (+weightKg !== profileData.weight[0].weightKg &&
      weightLb !== profileData.weight[0].weightLb)
  ) {
    const token = localStorage.token;
    try {
      const url = `${process.env.REACT_APP_API_URL}/api/profile/weight`;
      const config = {
        method: 'PUT',
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(weight),
      };
      const payload = yield call(fetchData, url, config);
      
      // Handle new response structure
      // If message field exists, weight was unchanged/older
      if (payload.message && payload.message.includes('unchanged')) {
        // Weight unchanged, no update needed
        yield put(addWeightToProfileSuccess(payload.profile || payload));
      } else {
        // Weight was updated - refresh nutrition after async calculation
        yield put(addWeightToProfileSuccess(payload));
        
        // Wait ~500ms for async nutrition recalculation, then refresh
        yield delay(600);
        yield put({ type: GET_NUTRITION });
        
        // Show success notification
        toast.success('✅ Weight updated! Your macros have been recalculated.', {
          position: 'top-right',
          duration: 5000,
        });
      }
    } catch (err) {
      const error = yield err.json();
      console.log(error);
      yield put(addWeightToProfileError(error));
      
      // Show error notification
      toast.error(error.error || 'Failed to update weight', {
        position: 'top-right',
        duration: 5000,
      });
    }
  }
}

export function* getAllOnsiteClients() {
  const token = localStorage.token;
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/profile/onsite-clients`;
    const config = {
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json',
      },
    };

    const payload = yield call(fetchData, url, config);
    yield put(getAllOnsiteClientsSuccess(payload));
  } catch (err) {
    const error = yield err.json();
    console.log(error);
    yield put(getAllOnsiteClientsError(error));
  }
}

export function* getUserFitbitNutrition() {
  try {
    const token = window.localStorage.token;
    const config = {
      headers: {
        'x-auth-token': token,
      },
    };
    let url = `${process.env.REACT_APP_API_URL}/auth/fitbit/getUserFitbitActivity`;
    let payload = yield call(fetchData, url, config);
    //modify date so it can be an iterable array in order to display as a card
    yield put(getUserFitbitNutritionSuccess(payload));
  } catch (err) {
    console.log(err);
  }
}

export function* getUserFitbitLogs() {
  try {
    const token = window.localStorage.token;
    const config = {
      headers: {
        'x-auth-token': token,
      },
    };
    let url = `${process.env.REACT_APP_API_URL}/auth/fitbit/me/all`;
    let payload = yield call(fetchData, url, config);
    yield put(getUserFitbitLogsSuccess(payload));
  } catch (err) {
    const error = yield err.json();
    console.log(err);
    yield put(getUserFitbitLogsError(error));
  }
}

export function* createUserMealPlan({ mealPlanData }) {
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/suggestic/getUserMealPlan`;
    const token = window.localStorage.token;
    const config = {
      method: 'POST',
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        calories: mealPlanData.calories,
        days: mealPlanData.days,
        format: mealPlanData.format,
      }),
    };
    let payload = yield call(fetchData, url, config);
    yield put(createUserMealPlanSuccess(payload));
  } catch (err) {
    console.log(err);
    const error = yield err.json();
    console.log(error);
    yield put(createUserMealPlanError(error));
  }
}

export function* createWeeklyMealPlan({ mealPlanData }) {
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/suggestic/createWeeklyMealPlan`;
    const token = window.localStorage.token;
    const config = {
      method: 'POST',
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        calories: mealPlanData.calories,
        format: mealPlanData.format,
        // kcalLimit: 0.4,
        // maxNumOfServings: 2,
        // maxServingWeight: 700,
        // minServingWeight: 100,
        // breakfastDistribution: 0.3
        // lunchDistribution: 0.3
        // dinnerDistribution: 0.3
        // snackDistribution: 0.2
      }),
    };
    let payload = yield call(fetchData, url, config);
    yield put(createWeeklyMealPlanSuccess(payload));
  } catch (err) {
    console.log(err);
    const error = yield err.json();
    console.log(error);
    yield put(createWeeklyMealPlanError(error));
  }
}

export function* getEquivalentRecipes({ recipeId, status }) {
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/suggestic/getEquivalentRecipes`;
    const token = window.localStorage.token;
    const config = {
      method: 'POST',
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ recipeId: recipeId }),
    };
    let payload = yield call(fetchData, url, config);
    const profile = yield select((state) => state.profile.profile);
    //adds equivalent recipes object to mealPlan recipe
    profile.currentMealPlan[status].map((meal) => {
      return meal.meals.map((recipe) => {
        if (recipe.recipe.id === recipeId) {
          recipe.alternativeRecipes = payload;
        }
      });
    });
    yield put(getEquivalentRecipesSuccess(profile, recipeId));
  } catch (err) {
    console.log(err);
    const error = yield err.json();
    console.log(error);
    yield put(getEquivalentRecipesError(error, recipeId));
  }
}

export function* swapMealPlanRecipe({ mealId, newRecipeId }) {
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/suggestic/swapMealPlanRecipe`;
    const token = window.localStorage.token;
    const config = {
      method: 'POST',
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ mealId: mealId, newRecipeId: newRecipeId }),
    };
    let payload = yield call(fetchData, url, config);
    yield put(swapMealPlanRecipeSuccess(payload, newRecipeId));
  } catch (err) {
    console.log(err);
    const error = yield err.json();
    console.log(error);
    yield put(swapMealPlanRecipeError(error, newRecipeId));
  }
}

export function* updateFoodInMealPlan({ foodData }) {
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/mealplan/updateFood`;
    const token = window.localStorage.token;
    const email =
      store.getState().auth.user && store.getState().auth.user.email;
    const config = {
      method: 'PUT',
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(foodData.foodData),
    };
    let payload = yield call(fetchData, url, config);
    yield all([
      put(updateFoodInMealPlanSuccess(payload, foodData.isProgramPage)),
      put(updateFoodInMealPlanLoadingSuccess()),
      put(getAllUserNotifications(email)),
    ]);
  } catch (err) {
    console.log(err);
    const error = yield err.json();
    console.log(error);
    yield put(updateFoodInMealPlanError(error));
  }
}

export function* getAlternativeFoods({ foodsString }) {
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/mealplan/alternatives?foods=${foodsString}`;
    const token = window.localStorage.token;
    const config = {
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json',
      },
    };
    let payload = yield call(fetchData, url, config);
    yield put(getAlternativeFoodsSuccess(payload));
  } catch (err) {
    console.log(err);
    const error = yield err.json();
    console.log(error);
    yield put(getAlternativeFoodsError(error));
  }
}

export function* updateUserProfile({ userInfo }) {
  const url = `${process.env.REACT_APP_API_URL}/api/users`;
  const config = {
    method: 'PUT',
    headers: {
      'x-auth-token': window.localStorage.token,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ profileImage: userInfo && userInfo.file }),
  };
  try {
    let payload = yield call(fetchData, url, config);
    yield put(updateUserProfileSuccess(payload));
    // yield call(loadUser());
  } catch (err) {
    const error = yield err.json();
    console.log(error);
    yield put(updateUserProfileError(error));
  }
}

export function* updateAssessmentProfile(payload) {
  const token = localStorage.token;
  const body = payload.assessmentData;
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/profile/assessment`;
    const config = {
      method: 'POST',
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    };
    const payload = yield call(fetchData, url, config);
    yield put(
      saveProfileSuccess({
        profile: payload,
      })
    );
    showToasterForProfileUpdate('🎉 Assessment data have been updated!');
  } catch (err) {
    console.log(err);
    const error = yield err.json();
    console.log(error);
    yield put(saveProfileError(error));
  }
}

export function* createNewClientUser({ userData }) {
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/users/createNewUser`;
    const token = window.localStorage.token;
    const config = {
      method: 'POST',
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user: userData,
      }),
    };
    let payload = yield call(fetchData, url, config);
    yield put(createNewUserSuccess(payload));
    // This saga serves every role the create-user modal can mint, not just
    // clients — a platform admin can add an organization admin through the
    // same form — so the copy follows the role instead of hardcoding
    // "Client". Previously creating an org admin announced "Client created".
    // `trainer` stays in the map because the endpoint still accepts it (the
    // Trainers tab owns that flow now, but other callers may not).
    const ROLE_NOUN = {
      client: 'Client',
      trainer: 'Trainer',
      organization_admin: 'Organization admin',
    };
    const noun = ROLE_NOUN[userData?.role] || 'User';
    // Only client and trainer have a roster with a Resend affordance to
    // point at; there is no organization-admin roster.
    const hasRosterResend =
      userData?.role === 'client' || userData?.role === 'trainer';
    // Surface the welcome-email outcome honestly. The backend returns
    // `isMailSent` reflecting whether sendWelcomeEmail actually went out
    // (routes/api/users.js /createNewUser). The modal previously toasted
    // an unconditional "successfully created" even when the email
    // silently failed — so a trainer could create a client, walk away,
    // and the client never received their invite. Mirror the Resend
    // pattern in AdminClientsPageV2: warn when the mail didn't send so the
    // trainer knows to resend from the roster.
    if (payload && payload.isMailSent) {
      toast.success(`${noun} created — welcome email sent.`, {
        position: 'top-right',
        duration: 5000,
      });
    } else if (payload) {
      // Got a response but the welcome email didn't go out — point the
      // trainer at the resend affordance.
      toast.warning(
        `${noun} created, but the welcome email may not have gone out.` +
          (hasRosterResend
            ? ' You can resend the invite from the roster.'
            : ''),
        { position: 'top-right', duration: 6000 },
      );
    } else {
      // Abnormal: a 2xx with no body. The account was still created, so
      // confirm success rather than wrongly blame the email.
      toast.success(`${noun} created.`, {
        position: 'top-right',
        duration: 5000,
      });
    }
    // Invalidate the trainer roster cache so the new client appears on
    // the next /dashboard/admin/clients visit without forcing a hard
    // reload. The clients page itself skips its on-mount fetch when
    // cached — this dispatch keeps that cache in sync.
    yield put({ type: GET_ALL_ONSITE_CLIENTS });
  } catch (err) {
    console.log(err);
    // A non-JSON failure (Heroku 503 / timeout HTML page) makes err.json()
    // throw too; without this the error action never fired, loading stayed
    // true and the invite modal couldn't be closed.
    let error;
    try {
      error = yield err.json();
    } catch (_) {
      error = { errors: [{ msg: "Couldn't reach Nubod. Try again in a moment." }] };
    }
    console.log(error);
    yield put(createNewUserError(error));
  }
}

const showToasterForProfileUpdate = (msg) => {
  toast.success(msg ? msg : '🎉 Your assessment data have been updated!', {
    position: 'top-right',
    duration: 5000,
  });
};

export default function* authSaga() {
  yield takeLatest(GET_PROFILE, getCurrentProfile);
  yield takeLatest(SAVE_PROFILE, saveProfile);
  yield takeLatest(SAVE_PROFILE_IGNORE_NUTRITION, saveProfileIgnoreNutrition);
  yield takeLatest(GET_NUTRITION, getNutrition);
  yield takeLatest(SAVE_NUTRITION, saveNutrition);
  yield takeLatest(ADD_WEIGHT_TO_PROFILE, addWeightToProfile);
  yield takeLatest(GET_ALL_ONSITE_CLIENTS, getAllOnsiteClients);
  yield takeLatest(GET_FITBIT_NUTRITION, getUserFitbitNutrition);
  yield takeLatest(GET_ALL_NUTRITION, getAllNutritionForUser);
  yield takeLatest(GET_USER_FITBIT_LOGS, getUserFitbitLogs);
  yield takeLatest(CREATE_USER_MEAL_PLAN, createUserMealPlan);
  yield takeLatest(CREATE_WEEKLY_MEAL_PLAN, createWeeklyMealPlan);
  yield takeLatest(GET_EQUIVALENT_RECIPES, getEquivalentRecipes);
  yield takeLatest(SWAP_MEAL_PLAN_RECIPE, swapMealPlanRecipe);
  yield takeLatest(UPDATE_FOOD_IN_MEAL_PLAN, updateFoodInMealPlan);
  yield takeLatest(GET_ALTERNATIVE_FOODS, getAlternativeFoods);
  yield takeLatest(UPDATE_USER_PROFILE, updateUserProfile);
  yield takeLatest(UPDATE_ASSESSMENT_PROFILE, updateAssessmentProfile);
  yield takeLatest(CREATE_NEW_USER, createNewClientUser);
}
